import { useEffect, useState } from "react";
import moment from "moment";
import useTranslator from "../../hooks/useTranslator";
import { getUserDisplayName } from "../../helper/common";
import { Avatar, Badge, Button, Checkbox, Modal, Notice } from "../design";
import "./ProfileUpdateRequestModal.css";

const REMINDER_WINDOW = 24 * 60 * 60 * 1000;

const formatDate = (value, currentLang) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat(currentLang, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
};

const TenantCard = ({ tenant, selected, onToggle, currentLang }) => {
  const { _t } = useTranslator();
  const tenantName =
    getUserDisplayName(tenant) ??
    tenant?.forensic_name ??
    tenant?.fname ??
    _t("tenant");
  const requestedAt = tenant?.reminderSentAt;
  const elapsed = requestedAt
    ? Date.now() - new Date(requestedAt).getTime()
    : REMINDER_WINDOW;
  const blocked =
    Boolean(requestedAt) &&
    Number.isFinite(elapsed) &&
    elapsed < REMINDER_WINDOW;
  const outdatedSince = formatDate(
    tenant?.profile_renewed_at ||
      tenant?.profile_completed_at ||
      tenant?.created_at,
    currentLang
  );

  return (
    <div className={`profile-update-tenant${blocked ? " is-disabled" : ""}`}>
      <Checkbox
        checked={selected}
        disabled={blocked}
        onChange={() => onToggle(tenant._id)}
        ariaLabel={tenantName}
      />
      <Avatar name={tenantName} size="md" />
      <div className="profile-update-tenant__info">
        <strong className="profile-update-tenant__name">{tenantName}</strong>
        <span className="profile-update-tenant__status">
          {requestedAt ? (
            blocked ? (
              <>
                {_t("profileReminded", "Reminded")}{" "}
                {moment(requestedAt)
                  .locale(currentLang === "es" ? "es" : "en-gb")
                  .fromNow()}{" "}
                ·{" "}
                {_t(
                  "profileReminderAvailableIn",
                  "available in {{N}}h",
                  undefined,
                  {
                    N: Math.max(
                      1,
                      Math.ceil((REMINDER_WINDOW - elapsed) / (60 * 60 * 1000))
                    ),
                  }
                )}
              </>
            ) : (
              <>
                {_t(
                  "profileUpdateRequestedOn",
                  "Requested {{date}}",
                  undefined,
                  {
                    date: formatDate(requestedAt, currentLang),
                  }
                )}{" "}
                · {_t("noResponseYet", "no response yet")}
              </>
            )
          ) : (
            <>
              {_t("profileOutdatedSince", "Outdated since")} {outdatedSince}
            </>
          )}
        </span>
      </div>
      <Badge tone="neutral">
        {_t(tenant?.role === "guarantor" ? "guarantor" : "tenant")}
      </Badge>
    </div>
  );
};

const ProfileUpdateRequestModal = ({
  isOpen,
  tenant,
  tenants,
  requestedAt,
  currentLang,
  isSending,
  onClose,
  onSend,
}) => {
  const { _t } = useTranslator();
  const [sendError, setSendError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const allTenants = (tenants || (tenant ? [tenant] : [])).map((item) => ({
    ...item,
    reminderSentAt: item.reminderSentAt || (!tenants ? requestedAt : null),
  }));
  const hasReminders =
    allTenants.some((item) => item?.reminderSentAt) || Boolean(requestedAt);
  const selectableTenants = allTenants.filter((item) => {
    if (!item?.reminderSentAt) return true;
    return (
      Date.now() - new Date(item.reminderSentAt).getTime() >= REMINDER_WINDOW
    );
  });

  useEffect(() => {
    if (isOpen) {
      setSendError("");
      setSelectedIds(selectableTenants.map((item) => item._id));
    }
  }, [isOpen, tenants, tenant, requestedAt]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!selectedIds.length) return;
    setSendError("");
    try {
      const sent = await onSend(selectedIds);
      if (sent !== false) onClose();
      else throw new Error("request failed");
    } catch {
      setSendError(
        _t(
          "profileUpdateRequestFailed",
          "Failed to send profile update request"
        )
      );
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={
        hasReminders
          ? _t("sendProfileUpdateRemindersTitle", "Send reminders")
          : _t("requestProfileUpdatesTitle", "Request profile updates")
      }
      description={
        hasReminders
          ? _t(
              "profileUpdateReminderSubtitle",
              "You already requested an update for these profiles. Send another nudge?"
            )
          : _t(
              "profileUpdateValidityDescription",
              "Profiles stay current for 3 months after each update."
            )
      }
      closeLabel={_t("close")}
      align="start"
      actionsLayout="end"
      actions={
        <>
          <Button variant="secondary" size="lg" onClick={onClose}>
            {_t("goBack", "Go Back")}
          </Button>
          <Button
            size="lg"
            loading={isSending}
            disabled={!selectedIds.length}
            onClick={handleSend}
          >
            {isSending
              ? `${_t("sending")}...`
              : hasReminders
                ? `${_t("sendProfileUpdateReminders", "Send reminders")} (${selectedIds.length})`
                : `${_t("sendProfileUpdateRequests", "Send requests")} (${selectedIds.length})`}
          </Button>
        </>
      }
    >
      <div className="profile-update-list">
        {allTenants.map((item) => (
          <TenantCard
            key={item._id}
            tenant={item}
            selected={selectedIds.includes(item._id)}
            onToggle={(id) =>
              setSelectedIds((ids) =>
                ids.includes(id)
                  ? ids.filter((itemId) => itemId !== id)
                  : [...ids, id]
              )
            }
            currentLang={currentLang}
          />
        ))}

        {hasReminders ? (
          <Notice tone="info">
            {_t(
              "profileUpdateReminderReassurance",
              "This just re-notifies them."
            )}{" "}
            <strong>{_t("noCreditsCharged", "No credits are charged.")}</strong>
          </Notice>
        ) : (
          <Notice
            tone="info"
            title={_t("whatHappensNext", "What happens next")}
          >
            <ol className="profile-update-steps">
              <li>
                {_t(
                  "eachPersonIsNotifiedToUpdateProfile",
                  "Each person is notified to update their profile."
                )}
              </li>
              <li>
                {_t(
                  "profileUpdateAccessRenewedDescription",
                  "Once updated, you get 3 more months of access to view the profile and generate reports."
                )}
              </li>
              <li>
                <strong>
                  {_t("noCreditsChargedNow", "No credits are charged now.")}
                </strong>{" "}
                {_t(
                  "profileUpdateChargeOnReopen",
                  "You'll be charged again per profile when it reopens."
                )}
              </li>
            </ol>
          </Notice>
        )}

        {sendError ? <Notice tone="danger">{sendError}</Notice> : null}
      </div>
    </Modal>
  );
};

export default ProfileUpdateRequestModal;
